import {Text} from '@dropbox/dig-components/typography';
import {Box, Stack} from '@dropbox/dig-foundations';
import {WorkstreamAuditLog as WorkstreamAuditLogType, WorkstreamUpdate} from 'client';
import {ChangeLogLine} from 'components/teams/TeamAuditLog';
import {t} from 'i18next';
import {formatUpdateDate} from 'views/workstreams/page/UpdateCard';

type UpdateLogValue = {
  id?: number;
  comment?: string;
  status?: string;
  summary?: string | null;
  summary_edited?: boolean;
};

const parseUpdateLogValue = (raw: string | null | undefined): UpdateLogValue | null => {
  try {
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

/**
 * Retypes an `update` log that only changed the summary as `update_summary`, so
 * Activity shows it as its own entry instead of folding it into the update's
 * edit history. Logs written before summaries existed have no `summary` key and
 * are left alone.
 */
export const asUpdateSummaryLog = (log: WorkstreamAuditLogType): WorkstreamAuditLogType => {
  if (log.type !== 'update') {
    return log;
  }
  const before = parseUpdateLogValue(log.before);
  const after = parseUpdateLogValue(log.after);
  if (
    !before ||
    !after?.id ||
    before.comment !== after.comment ||
    before.status !== after.status ||
    (before.summary ?? null) === (after.summary ?? null)
  ) {
    return log;
  }
  return {...log, type: 'update_summary'};
};

const summaryChangeTitle = (before: UpdateLogValue | null, after: UpdateLogValue | null) => {
  if (!after?.summary) {
    return t('workstream_audit_update_summary_reset');
  }
  // Only logs written since summary_edited was added say who wrote the old one.
  if (before?.summary && before.summary_edited === false) {
    return t('workstream_audit_update_summary_replaced_ai');
  }
  return t('workstream_audit_update_summary_edited');
};

export const UpdateSummaryChanges = ({
  changes,
  updates,
}: {
  changes: WorkstreamAuditLogType[];
  updates: WorkstreamUpdate[];
}) => (
  <Stack gap="8">
    {changes.map((log) => {
      const before = parseUpdateLogValue(log.before);
      const after = parseUpdateLogValue(log.after);
      const update = updates.find(({id}) => id === after?.id);
      return (
        <Stack key={`update_summary-${after?.id}-${log.date}`} gap="2">
          <Box marginBottom="6">
            <Text isBold>{summaryChangeTitle(before, after)}</Text>
            {update && (
              <Text size="small" color="subtle" tagName="div">
                {t('workstream_audit_update_summary_of', {
                  date: formatUpdateDate(update.created_at),
                })}
              </Text>
            )}
          </Box>
          {before?.summary && (
            <ChangeLogLine change="remove" type="update_summary" value={before.summary} />
          )}
          {after?.summary && (
            <ChangeLogLine change="add" type="update_summary" value={after.summary} />
          )}
        </Stack>
      );
    })}
  </Stack>
);
